import React, { useCallback, useEffect, useState, useContext } from 'react';
import { IconButton, Button } from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from '@mui/icons-material/Download';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Dialog, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import AuthContext from "../../../../context/auth-context"
import axios from 'axios'
import { mapDocumentTemplateTypeName, mapFlagName, mapSemanticNegativeValue, getLatestRuleEngineEvent, splitTriggeredRules, getSameIpMatches, getFaceValidationScoreColor, triggerFileDownload } from './signer_analysis_utils';
import { ruleOptions, mapCrossCheckFieldName, describeCrossCheckResult } from '../../../RulesPage/rules_utils';
import SignerLoginMap, { getLoginPosition } from './SignerLoginMap';
import { formatISOZuluDate, formatCPF } from '../../../../utils/utils';

const downloadButtonSx = { marginTop: '10px', color: '#0b1f82', borderColor: '#0b1f82' }

function AttachmentDownloadButton({ label, disabled, onClick }) {
    return (
        <div>
            <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                sx={downloadButtonSx}
                disabled={disabled}
                onClick={onClick}
            >
                {label}
            </Button>
        </div>
    );
}

function SignerAnalysisDataDialog(props) {

    const {
        open,
        setOpen,
        signerAnalyzing,
        setSignerAnalyzing,
    } = props

    const statesToInitial = useCallback(() => {
        setSignerAnalyzing(undefined)
    }, [setSignerAnalyzing])

    const onCloseDialog = useCallback(() => {
        statesToInitial()
        setOpen(false)
    }, [statesToInitial, setOpen])

    const [dialogSize, setDialogSize] = useState("lg")
    const [faceReconImageUrl, setFaceReconImageUrl] = useState(null)
    const [ocrImagesUrls, setOcrImagesUrls] = useState(null)
    const [hasAttachments, SetHasAttachments] = useState(false)
    const [downloadingAttachment, setDownloadingAttachment] = useState(null)
    const userRoles = useContext(AuthContext).user_data.roles

    const downloadAttachment = useCallback((attachmentId, endpoint, getDownloadUrl) => {
        setDownloadingAttachment(attachmentId)
        axios.get(endpoint, { params: { download: true } })
            .then((response) => {
                triggerFileDownload(getDownloadUrl(response.data))
            })
            .catch((error) => {
            })
            .finally(() => {
                setDownloadingAttachment(null)
            })
    }, [])

    const downloadSelfie = () => {
        downloadAttachment('selfie', `/dash/sign/signer/${signerAnalyzing.signer_key}/face_recognition`, (data) => data.url)
    }

    const downloadDocument = (index) => {
        downloadAttachment(`document_${index}`, `/dash/sign/signer/${signerAnalyzing.signer_key}/ocr`, (data) => data.urls[index])
    }

    const fetchFaceReconImage = useCallback(() => {
        const timer = setTimeout(() => {
            axios.get(`/dash/sign/signer/${signerAnalyzing.signer_key}/face_recognition`)
                .then((response) => {
                    setFaceReconImageUrl(response.data.url)
                    setDialogSize("lg")
                    SetHasAttachments(true)
                })
                .catch((error) => {
                })
                .finally(() => {
                })
        }, 500)
        return () => {
            clearTimeout(timer)
        }
    }, [signerAnalyzing.signer_key])

    const fetchOCRUrls = useCallback(() => {
        const timer = setTimeout(() => {
            axios.get(`/dash/sign/signer/${signerAnalyzing.signer_key}/ocr`)
                .then((response) => {
                    setOcrImagesUrls(response.data.urls)
                    setDialogSize("lg")
                    SetHasAttachments(true)
                })
                .catch((error) => {
                })
                .finally(() => {
                })
        }, 500)
        return () => {
            clearTimeout(timer)
        }
    }, [signerAnalyzing.signer_key])

    useEffect(() => {
        const cleanups = []
        if (userRoles.includes("read_signer_images")) {
            if (signerAnalyzing.signer_analysis?.liveness) {
                cleanups.push(fetchFaceReconImage())
            }
            if (signerAnalyzing.signer_analysis?.facematch_document) {
                cleanups.push(fetchOCRUrls())
            }
        }
        return () => cleanups.forEach((cleanup) => cleanup())
    }, [userRoles, fetchFaceReconImage, fetchOCRUrls, signerAnalyzing])

    const hasNegativeValues = (document_data) => {
        return Object.entries(document_data || {}).some(
            ([flag, value]) => mapSemanticNegativeValue(flag) === value
        );
    }

    const translateReasonDescription = (reasonDescription) => {
        let reasonTranslation = '-'
        for (let option of ruleOptions){
            if (reasonDescription.includes(option.step)) {
                reasonTranslation = option.name
                break
            }
        }

        if (reasonDescription.includes('insufficient')) {
            reasonTranslation = 'Dados insuficientes - ' + reasonTranslation
        }
        return reasonTranslation
    }

    const eventsWithReason =
    (signerAnalyzing.rule_analysis_events_data || [])
        .filter((event) => event?.reason_description);
    const hasReason = eventsWithReason.length > 0;

    const translateTriggeredRule = (triggeredRule) => {
        let ruleTranslation = triggeredRule.rule
        for (let option of ruleOptions) {
            if (option.step === triggeredRule.rule) {
                ruleTranslation = option.name
                break
            }
        }
        if (triggeredRule.insufficient_data) {
            ruleTranslation = 'Dados insuficientes - ' + ruleTranslation
        }
        if (triggeredRule.source === 'internal_rules') {
            ruleTranslation = ruleTranslation + ' (interna)'
        }
        return ruleTranslation
    }

    const latestRuleEngineEvent = getLatestRuleEngineEvent(signerAnalyzing.rule_analysis_events_data)
    const triggeredRules = splitTriggeredRules(latestRuleEngineEvent)
    const sameIpMatches = getSameIpMatches(latestRuleEngineEvent)
    const sameIpDistinctIps = sameIpMatches ? [...new Set(sameIpMatches.map((sameIpMatch) => sameIpMatch.ip))] : []
    const sameIpHeaderCellStyle = { fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap", backgroundColor: "#f5f6fa" }
    const sameIpCellStyle = { fontSize: "14px", whiteSpace: "nowrap" }

    const eventsWithCrossCheck =
    (signerAnalyzing.rule_analysis_events_data || [])
        .filter((event) => Array.isArray(event?.cross_check));
    const hasCrossCheck = eventsWithCrossCheck.length > 0;
    const crossCheckResults = hasCrossCheck
        ? eventsWithCrossCheck[eventsWithCrossCheck.length - 1].cross_check
        : [];

    return (
        <Dialog
            open={open}
            onClose={() => onCloseDialog()}
            fullWidth
            maxWidth={dialogSize}
        >

            {
                signerAnalyzing.signer_analysis
                    ?
                    <div className="dialog__column-container">
                        <header style={{ marginBottom: '10px' }}>
                            <h1 style={{ fontSize: '22px', color: "#0b1f82" }}>Dados de assinatura</h1>
                            <IconButton
                                onClick={() => onCloseDialog()}
                            >
                                <CloseIcon />
                            </IconButton>
                        </header>
                        <div style={{ marginBottom: '20px' }}>
                            <p>
                                <span style={{ fontSize: '16px' }}><strong>Nome:</strong></span>
                                <span style={{ fontSize: '16px', marginLeft: '10px' }}>{signerAnalyzing.name}</span>
                            </p>
                            {triggeredRules && triggeredRules.reproved.length > 0 && (
                            <p style={{ marginTop: '10px' }}>
                                <span style={{ fontSize: '16px' }}>
                                <strong>Regras que reprovaram:</strong>
                                </span>
                                <span style={{ fontSize: '16px', marginLeft: '10px' }}>
                                {triggeredRules.reproved.map((triggeredRule) => translateTriggeredRule(triggeredRule)).join(' | ')}
                                </span>
                            </p>
                            )}
                            {triggeredRules && triggeredRules.manual.length > 0 && (
                            <p style={{ marginTop: '10px' }}>
                                <span style={{ fontSize: '16px' }}>
                                <strong>Regras que enviaram para análise manual:</strong>
                                </span>
                                <span style={{ fontSize: '16px', marginLeft: '10px' }}>
                                {triggeredRules.manual.map((triggeredRule) => translateTriggeredRule(triggeredRule)).join(' | ')}
                                </span>
                            </p>
                            )}
                            {!triggeredRules && hasReason && (
                            <p style={{ marginTop: '10px' }}>
                                <span style={{ fontSize: '16px' }}>
                                <strong>Motivo de derivação:</strong>
                                </span>
                                <span style={{ fontSize: '16px', marginLeft: '10px' }}>
                                {translateReasonDescription(eventsWithReason[0].reason_description)}
                                </span>
                            </p>
                            )}
                        </div>
                        <div className='inbox__body_d_block'>
                            {
                                getLoginPosition(signerAnalyzing) &&
                                <div style={{ marginBottom: '20px' }}>
                                    <SignerLoginMap signer={signerAnalyzing} />
                                </div>
                            }
                            {
                                signerAnalyzing.signer_analysis?.liveness &&
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '80%' }}>
                                            <span style={{ fontSize: '16px', color: "#0b1f82" }}>
                                                <strong>Liveness</strong>
                                            </span>
                                            <p style={{ fontSize: '14px', marginTop: '5px' }}>
                                                O rosto foi validado na análise de liveness?
                                            </p>
                                        </div>
                                        {
                                            signerAnalyzing.signer_analysis?.liveness?.status === 'live' &&
                                            <div style={{
                                                padding: '10px',
                                                fontSize: '14px',
                                                color: 'green',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                OK
                                            </div>
                                        }
                                        {
                                            signerAnalyzing.signer_analysis?.liveness?.status !== 'live' &&
                                            <div style={{
                                                padding: '10px',
                                                fontSize: '14px',
                                                color: 'red',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                Não
                                            </div>
                                        }

                                    </div>
                                    {
                                        signerAnalyzing.signer_analysis?.liveness.flags &&
                                        <div style={{ marginBottom: '20px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '80%' }}>
                                                <span style={{ fontSize: '16px', color: "#0b1f82" }}>
                                                    <strong>Liveness - Indicadores</strong>
                                                </span>
                                                <p style={{ fontSize: '14px', marginTop: '5px' }}>
                                                    Indicadores detectados durante a sessão de liveness:
                                                </p>
                                                {
                                                    hasNegativeValues(signerAnalyzing.signer_analysis?.liveness?.flags) ?
                                                        (
                                                            <ul style={{ fontSize: '14px', marginTop: '10px', marginLeft: '40px' }}>
                                                                {Object.entries(signerAnalyzing.signer_analysis?.liveness?.flags || {}).map(([flag, value]) =>
                                                                    mapFlagName(flag) && mapSemanticNegativeValue(flag) === value && (
                                                                        <li style={{ marginBottom: '5px' }} key={flag}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                                                <span>{mapFlagName(flag)}</span>
                                                                                <ErrorOutlineIcon style={{ color: 'red' }} />
                                                                            </div>
                                                                        </li>
                                                                    )
                                                                )}
                                                            </ul>
                                                        ) : <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '10px' }}>
                                                            <p style={{ fontSize: '14px'}}>
                                                                Nennhum indicador negativo detectado
                                                            </p>
                                                            <CheckCircleOutlineIcon style={{ color: 'green' }} />
                                                        </div>
                                                }
                                            </div>
                                        </div>
                                    }
                                </div>
                            }

                            {
                                (signerAnalyzing.ocr || []).length > 0 &&
                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '80%' }}>
                                        <span style={{ fontSize: '16px', color: "#0b1f82" }}>
                                            <strong>OCR - Indicadores</strong>
                                        </span>
                                        <p style={{ fontSize: '14px', marginTop: '5px' }}>
                                            Indicadores detectados no processo de OCR de documento:
                                        </p>
                                        {
                                            (signerAnalyzing.ocr || []).filter(document => document.document_data)
                                                .map(document => (

                                                    <div style={{ marginLeft: '10px', marginTop: '10px' }}>
                                                        <span style={{ fontSize: '14px' }}>
                                                            <strong>{mapDocumentTemplateTypeName(document.template_type)}</strong>
                                                        </span>
                                                        {
                                                            hasNegativeValues(document.document_data)
                                                                ?
                                                                <ul style={{ fontSize: '14px', marginTop: '5px', marginLeft: '30px' }}>
                                                                    {Object.entries(document.document_data || {}).map(([flag, value]) =>
                                                                        mapFlagName(flag) && mapSemanticNegativeValue(flag) === value && (
                                                                            <li style={{ marginBottom: '5px' }} key={flag}>
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                                                    <span>{mapFlagName(flag)}</span>
                                                                                    <ErrorOutlineIcon style={{ color: 'red' }} />
                                                                                </div>
                                                                            </li>
                                                                        )
                                                                    )}
                                                                </ul> : <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '10px'  }}>
                                                                    <p style={{ fontSize: '14px'}}>
                                                                        Nennhum indicador negativo detectado
                                                                    </p>
                                                                    <CheckCircleOutlineIcon style={{ color: 'green' }} />
                                                                </div>
                                                        }
                                                    </div>
                                                ))
                                        }
                                    </div>
                                </div>
                            }

                            {
                                sameIpMatches &&
                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: '16px', color: "#0b1f82" }}>
                                            <strong>Mesmo IP com diferentes CPFs</strong>
                                        </span>
                                        {
                                            sameIpMatches.length > 0 ?
                                                <>
                                                    <p style={{ fontSize: '14px', marginTop: '5px' }}>
                                                        IP: <strong>{sameIpDistinctIps.join(' | ')}</strong>
                                                    </p>
                                                    <p style={{ fontSize: '14px', marginTop: '5px' }}>
                                                        Envelopes desta empresa que compartilham o IP e dispararam a regra:
                                                    </p>
                                                    <TableContainer component={Paper} style={{ marginTop: '10px', maxHeight: '360px' }}>
                                                        <Table size="small" stickyHeader>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell sx={sameIpHeaderCellStyle}>Assinante</TableCell>
                                                                    <TableCell sx={sameIpHeaderCellStyle}>CPF</TableCell>
                                                                    <TableCell sx={sameIpHeaderCellStyle}>Envelope</TableCell>
                                                                    <TableCell sx={sameIpHeaderCellStyle}>ID externo</TableCell>
                                                                    <TableCell sx={sameIpHeaderCellStyle}>Facial em</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {
                                                                    sameIpMatches.map((sameIpMatch) => (
                                                                        <TableRow key={`${sameIpMatch.ip}-${sameIpMatch.signer_key}`} hover>
                                                                            <TableCell sx={{ fontSize: "14px" }}>{sameIpMatch.signer_name}</TableCell>
                                                                            <TableCell sx={sameIpCellStyle}>{sameIpMatch.signer_document_number ? formatCPF(sameIpMatch.signer_document_number) : '-'}</TableCell>
                                                                            <TableCell sx={{ fontSize: "14px", maxWidth: "240px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                                <a
                                                                                    href={`/envelope/${sameIpMatch.envelope_key}`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    title={sameIpMatch.envelope_subject || sameIpMatch.envelope_key}
                                                                                >
                                                                                    {sameIpMatch.envelope_subject || sameIpMatch.envelope_key}
                                                                                </a>
                                                                            </TableCell>
                                                                            <TableCell sx={sameIpCellStyle}>{sameIpMatch.envelope_external_id || '-'}</TableCell>
                                                                            <TableCell sx={sameIpCellStyle}>{sameIpMatch.face_recognition_at ? formatISOZuluDate(sameIpMatch.face_recognition_at) : '-'}</TableCell>
                                                                        </TableRow>
                                                                    ))
                                                                }
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </> : <p style={{ fontSize: '14px', marginTop: '5px' }}>
                                                    Nenhum envelope desta empresa compartilha o IP; a coincidência foi com envelopes de fora da empresa.
                                                </p>
                                        }
                                    </div>
                                </div>
                            }

                            {
                                hasCrossCheck &&
                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '80%' }}>
                                        <span style={{ fontSize: '16px', color: "#0b1f82" }}>
                                            <strong>Cruzamento de dados básicos</strong>
                                        </span>
                                        <p style={{ fontSize: '14px', marginTop: '5px' }}>
                                            Campos da regra cruzados entre a proposta, o documento e a base de dados:
                                        </p>
                                        {
                                            crossCheckResults.length > 0 ?
                                                <ul style={{ fontSize: '14px', marginTop: '10px', marginLeft: '40px' }}>
                                                    {
                                                        crossCheckResults.map((crossCheckResult) => (
                                                            <li style={{ marginBottom: '5px' }} key={`${crossCheckResult.field}-${crossCheckResult.result}`}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                                    <span><strong>{mapCrossCheckFieldName(crossCheckResult.field)}</strong>: {describeCrossCheckResult(crossCheckResult)}</span>
                                                                    {
                                                                        crossCheckResult.result === 'matched'
                                                                            ? <CheckCircleOutlineIcon style={{ color: 'green' }} />
                                                                            : <ErrorOutlineIcon style={{ color: 'red' }} />
                                                                    }
                                                                </div>
                                                            </li>
                                                        ))
                                                    }
                                                </ul> : <p style={{ fontSize: '14px', marginTop: '10px' }}>
                                                    Nenhum campo tinha fontes suficientes para comparação
                                                </p>
                                        }
                                    </div>
                                </div>
                            }

                            {
                                signerAnalyzing.signer_analysis?.facematch_document &&
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '80%' }}>
                                        <span style={{ fontSize: '16px', color: "#0b1f82" }}>
                                            <strong>Face Match de Documento</strong>
                                        </span>
                                        <p style={{ fontSize: '14px', marginTop: '5px' }}>
                                            A foto do documento bate com a foto do bureau de validação de face?
                                        </p>
                                    </div>
                                    <div style={{
                                        padding: '10px',
                                        fontSize: '14px',
                                        color: 'blue',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {signerAnalyzing.signer_analysis?.facematch_document?.similarity_ratio}%
                                    </div>
                                </div>
                            }

                            {
                                (signerAnalyzing.signer_analysis?.registration?.face_validation_data || []).length > 0 &&
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '80%' }}>
                                        <span style={{ fontSize: '16px', color: "#0b1f82" }}>
                                            <strong>Validação de Faces</strong>
                                        </span>
                                        <p style={{ fontSize: '14px', marginTop: '5px' }}>
                                            A foto do rosto confere com o CPF informado?
                                        </p>
                                    </div>
                                    <div style={{
                                        padding: '10px',
                                        fontSize: '14px',
                                        color: getFaceValidationScoreColor(signerAnalyzing.signer_analysis?.registration?.face_validation_data[0].score),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {signerAnalyzing.signer_analysis?.registration?.face_validation_data[0].score}%
                                    </div>
                                </div>
                            }

                            {
                                signerAnalyzing.signer_analysis?.registration?.fraud_face_matches &&
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '80%' }}>
                                        <span style={{ fontSize: '16px', color: "#0b1f82" }}>
                                            <strong>Validação de Fraude</strong>
                                        </span>
                                        <p style={{ fontSize: '14px', marginTop: '5px' }}>
                                            O rosto foi encontrado na base de fraudadores?
                                        </p>
                                    </div>
                                    {
                                        signerAnalyzing.signer_analysis?.registration?.fraud_face_matches?.length > 0 &&
                                        <div style={{
                                            padding: '10px',
                                            fontSize: '14px',
                                            color: 'red',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            Sim
                                        </div>
                                    }
                                    {
                                        signerAnalyzing.signer_analysis?.registration?.fraud_face_matches?.length === 0 &&
                                        <div style={{
                                            padding: '10px',
                                            fontSize: '14px',
                                            color: 'green',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            Não
                                        </div>
                                    }
                                </div>
                            }
                            {
                                userRoles.includes("read_signer_images") && hasAttachments
                                && <div style={{ marginTop: '40px', marginBottom: '20px', textAlign: 'center' }}>
                                    <span style={{ fontSize: '16px', color: "#0b1f82" }}>
                                        <strong>Anexos do assinante</strong>
                                    </span>
                                    {
                                        faceReconImageUrl &&
                                        <div style={{ marginTop: '20px', marginBottom: '20px', textAlign: 'center' }}>
                                            <img
                                                style={{ margin: "10px auto auto auto", borderRadius: "5px", maxWidth: "100%", maxHeight: "70vh", height: "auto", objectFit: "contain" }}
                                                src={faceReconImageUrl}
                                                alt="face_recognition_image"
                                            />
                                            <AttachmentDownloadButton
                                                label="Baixar selfie"
                                                disabled={downloadingAttachment === 'selfie'}
                                                onClick={downloadSelfie}
                                            />
                                        </div>

                                    }
                                    {
                                        ocrImagesUrls &&
                                        <div style={{ marginTop: '40px', marginBottom: '20px', textAlign: 'center' }}>
                                            {
                                                ocrImagesUrls.map((imgUrl, index) => (
                                                    <div key={imgUrl} style={{ marginBottom: '20px' }}>
                                                        <img
                                                            style={{ margin: "10px auto auto auto", borderRadius: "5px", maxWidth: "100%", maxHeight: "70vh", height: "auto", objectFit: "contain" }}
                                                            src={imgUrl}
                                                            alt="ocr_image_url"
                                                        />
                                                        <AttachmentDownloadButton
                                                            label="Baixar documento"
                                                            disabled={downloadingAttachment === `document_${index}`}
                                                            onClick={() => downloadDocument(index)}
                                                        />
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    }
                                </div>
                            }

                        </div>
                    </div>
                    : <div className="dialog__column-container">
                        <header style={{ marginBottom: '10px' }}>
                            <h1 style={{ fontSize: '22px' }}>Dados de assinatura</h1>
                            <IconButton
                                onClick={() => onCloseDialog()}
                            >
                                <CloseIcon />
                            </IconButton>
                        </header>
                        <div style={{ marginBottom: '20px' }}>
                            <p>
                                <span style={{ fontSize: '16px' }}><strong>Nome:</strong></span>
                                <span style={{ fontSize: '16px', marginLeft: '10px' }}>{signerAnalyzing.name}</span>
                            </p>
                        </div>
                        <div className='inbox__body_d_block'>
                            <span className="signer-data-dialog__error error-message">
                                Não foi possível obter os dados de assinatura
                            </span>
                            {
                                getLoginPosition(signerAnalyzing) &&
                                <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                                    <SignerLoginMap signer={signerAnalyzing} />
                                </div>
                            }
                        </div>
                    </div>

            }
        </Dialog>
    );
}

export default SignerAnalysisDataDialog;